import { useRef } from "react";
import { useUnmount } from "react-use";
import "@tensorflow/tfjs-core";
import "@tensorflow/tfjs-converter";
import "@tensorflow/tfjs-backend-webgl";
import * as bodySegmentation from "@tensorflow-models/body-segmentation";
import { BodySegmenter } from "@tensorflow-models/body-segmentation";
import createDeferred from "lib/deferredPromise";
import { removeTracksFromStream } from "lib/mediaStreamHelpers";

export type BackgroundMode = "none" | "blur" | "image";

export default function useBlurController() {
  const foregroundThreshold = 0.7;
  const backgroundBlurAmount = 15;
  const edgeBlurAmount = 3;
  const flipHorizontal = false;
  const controller = useRef({
    segmenter: null as BodySegmenter | null,
    canvas: document.createElement("canvas"),
    video: document.createElement("video"),
    drawing: null as Promise<void> | null,
    localStream: null as MediaStream | null,
    backgroundImage: null as HTMLImageElement | null,
    backgroundMode: "blur" as BackgroundMode,
    offscreenCanvas: document.createElement("canvas"),
  });

  const initController = async () => {
    const model = bodySegmentation.SupportedModels.MediaPipeSelfieSegmentation;
    // Use the TFJS runtime, not 'mediapipe': the installed
    // @mediapipe/selfie_segmentation is a UMD/global bundle with no real ES
    // named export, so under CRA4/webpack ESM interop `new SelfieSegmentation`
    // throws "i.SelfieSegmentation is not a constructor" and blur never starts.
    // The TFJS runtime (tfjs-core/webgl + tfjs-converter, all bundled) runs the
    // same model without that constructor.
    //
    // NOTE: with no modelUrl the converter fetches the graph model from tfhub.dev
    // (google storage) on first use — one network round-trip, then browser-cached.
    // So the FIRST blur toggle needs connectivity and will fail under a strict CSP
    // or offline; blurStart's caller must handle a rejected initController (the
    // toggle no-ops and blur stays off). TODO: self-host the model under PUBLIC_URL
    // and pass modelUrl to drop the runtime dependency on tfhub.dev.
    const segmenter = await bodySegmentation.createSegmenter(model, {
      runtime: "tfjs",
      modelType: "general",
    });

    // @ts-ignore
    controller.current.video.playsInline = true;
    controller.current.segmenter = segmenter;
  };

  const videoPlay = async () => {
    const deferred = createDeferred();
    const onCanPlay = () => {
      deferred.resolve();
      controller.current.video.removeEventListener("canplay", onCanPlay);
    };
    controller.current.video.addEventListener("canplay", onCanPlay);
    const timer = setTimeout(async () => {
      await clear();
      deferred.reject();
    }, 1000);
    await deferred.promise;
    clearTimeout(timer);
    await controller.current.video.play();
  };

  const drawFrameBlur = async () => {
    if (!controller.current.segmenter || !controller.current.video.srcObject) return;
    const segmentation = await controller.current.segmenter.segmentPeople(controller.current.video, { flipHorizontal });
    return bodySegmentation.drawBokehEffect(
      controller.current.canvas,
      controller.current.video,
      segmentation,
      foregroundThreshold,
      backgroundBlurAmount,
      edgeBlurAmount,
      flipHorizontal
    );
  };

  const drawFrameVirtualBg = async () => {
    if (!controller.current.segmenter || !controller.current.video.srcObject) return;
    const { canvas, video, backgroundImage } = controller.current;
    if (!backgroundImage) return drawFrameBlur();

    const segmentation = await controller.current.segmenter.segmentPeople(video, { flipHorizontal });
    if (!segmentation.length) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const mask = segmentation[0];
    const maskData = await mask.mask.toImageData();

    // Draw background image
    ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);

    // Reuse offscreen canvas for compositing
    const offscreen = controller.current.offscreenCanvas;
    if (offscreen.width !== canvas.width || offscreen.height !== canvas.height) {
      offscreen.width = canvas.width;
      offscreen.height = canvas.height;
    }
    const offCtx = offscreen.getContext("2d");
    if (!offCtx) return;
    offCtx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Apply mask: keep person pixels from video, rest stays as background
    const bgPixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const fgPixels = offCtx.getImageData(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < maskData.data.length; i += 4) {
      const maskAlpha = maskData.data[i]; // person confidence (R channel)
      const pixelIdx = i;
      const confidence = maskAlpha / 255;

      if (confidence > foregroundThreshold) {
        // Person pixel — use video frame
        bgPixels.data[pixelIdx] = fgPixels.data[pixelIdx];
        bgPixels.data[pixelIdx + 1] = fgPixels.data[pixelIdx + 1];
        bgPixels.data[pixelIdx + 2] = fgPixels.data[pixelIdx + 2];
        bgPixels.data[pixelIdx + 3] = 255;
      } else if (confidence > foregroundThreshold - 0.1) {
        // Edge blending
        const blend = (confidence - (foregroundThreshold - 0.1)) / 0.1;
        bgPixels.data[pixelIdx] = Math.round(fgPixels.data[pixelIdx] * blend + bgPixels.data[pixelIdx] * (1 - blend));
        bgPixels.data[pixelIdx + 1] = Math.round(fgPixels.data[pixelIdx + 1] * blend + bgPixels.data[pixelIdx + 1] * (1 - blend));
        bgPixels.data[pixelIdx + 2] = Math.round(fgPixels.data[pixelIdx + 2] * blend + bgPixels.data[pixelIdx + 2] * (1 - blend));
        bgPixels.data[pixelIdx + 3] = 255;
      }
      // else: background pixel — keep bgPixels as-is (background image)
    }

    ctx.putImageData(bgPixels, 0, 0);
  };

  const drawFrame = async () => {
    if (controller.current.backgroundMode === "image" && controller.current.backgroundImage) {
      return drawFrameVirtualBg();
    }
    return drawFrameBlur();
  };

  const drawFrames = async () => {
    if (!controller.current.drawing) {
      return;
    }

    while (controller.current.drawing) {
      const now = Date.now();
      controller.current.drawing = drawFrame();
      await controller.current.drawing;
      const frameRenderTime = Date.now() - now;
      await new Promise<void>((r) => setTimeout(() => r(), Math.max(0, 40 - frameRenderTime)));
    }
  };

  const clear = async () => {
    await controller.current.drawing;

    controller.current.video.pause();
    controller.current.drawing = null;
    controller.current.video.srcObject = null;
    if (controller.current.localStream) {
      removeTracksFromStream(controller.current.localStream, controller.current.localStream.getTracks());
    }
    controller.current.localStream = null;
  };

  const blurStart = async (videoTrack: MediaStreamTrack): Promise<MediaStream> => {
    if (!controller.current.segmenter) await initController();

    controller.current.localStream = new MediaStream([videoTrack.clone()]);
    controller.current.video.srcObject = controller.current.localStream;

    const { width = 640, height = 480 } = videoTrack.getSettings();

    controller.current.canvas.width = width;
    controller.current.canvas.height = height;

    await videoPlay().then(() => {
      controller.current.drawing = Promise.resolve();
      drawFrames();
    });
    videoTrack.stop();
    // @ts-ignore
    return controller.current.canvas.captureStream(30);
  };

  const blurStop = async (): Promise<MediaStream> => {
    const newStream = new MediaStream(controller.current.localStream?.getTracks().map((t) => t.clone()) ?? []);
    await clear();

    return newStream;
  };

  const setBackgroundImage = (imageUrl: string | null) => {
    if (!imageUrl) {
      controller.current.backgroundImage = null;
      controller.current.backgroundMode = "blur";
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      controller.current.backgroundImage = img;
      controller.current.backgroundMode = "image";
    };
    img.src = imageUrl;
  };

  const setBackgroundMode = (mode: BackgroundMode) => {
    controller.current.backgroundMode = mode;
    if (mode !== "image") {
      controller.current.backgroundImage = null;
    }
  };

  useUnmount(() => {
    clear().then();
  });

  return {
    blurStart,
    blurStop,
    setBackgroundImage,
    setBackgroundMode,
  };
}
